// Threads (signature piece): shadcn Message / Item / Collapsible / Marker restyled to the Paper
// "Family" thread — Slack-style rows (avatar, author + time, then text), no bubbles.
import { ChevronRight, Check } from "lucide-react";
import type { ReactNode } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@crew/ui/components/collapsible";
import { Item, ItemContent, ItemMedia, ItemTitle } from "@crew/ui/components/item";
import { Link } from "@crew/ui/components/link";
import { Marker, MarkerContent, MarkerIcon } from "@crew/ui/components/marker";
import { Message, MessageContent, MessageHeader } from "@crew/ui/components/message";
import { TasksIcon } from "@crew/ui/components/nav-icons";
import { toneText, type Tone } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";

export type ThreadSize = "md" | "sm";

/**
 * One thread message. `size="sm"` is the side-panel density; `highlight` is the tinted band
 * for a thread's opening message; `continued` follows on from the author's message above,
 * without the avatar and header (a reply under the task cards its turn made).
 */
export function ThreadMessage({
  avatar,
  author,
  time,
  timeTone = "muted",
  size = "md",
  highlight = false,
  continued = false,
  children,
  className,
}: {
  avatar: ReactNode;
  author: ReactNode;
  time?: ReactNode;
  timeTone?: Tone;
  size?: ThreadSize;
  highlight?: boolean;
  continued?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const sm = size === "sm";
  return (
    <Message
      data-slot="thread-message"
      className={cn(
        sm ? "gap-2.5" : "gap-3",
        highlight && "border-b border-border bg-foreground/[0.024] px-5 pt-4 pb-3.5",
        className,
      )}
    >
      <div className={cn("shrink-0", continued && "invisible h-0")}>{avatar}</div>
      <MessageContent className={cn(sm ? "gap-1.5" : "gap-1.5")}>
        {continued ? null : (
          <MessageHeader className="gap-2 px-0 text-xs font-normal">
            <span className={cn("font-semibold text-foreground", sm ? "text-meta" : "text-nav")}>
              {author}
            </span>
            {time ? <span className={cn("text-xs", toneText[timeTone])}>{time}</span> : null}
          </MessageHeader>
        )}
        {children}
      </MessageContent>
    </Message>
  );
}

export function ThreadText({ size = "md", children }: { size?: ThreadSize; children: ReactNode }) {
  return (
    <p
      className={cn("text-foreground", size === "sm" ? "text-[14px] leading-[21px]" : "text-body")}
    >
      {children}
    </p>
  );
}

/** Copper @mention inside message text. */
export function Mention({ children }: { children: ReactNode }) {
  return <span className="rounded-sm bg-copper/10 px-1 font-medium text-copper">@{children}</span>;
}

type TaskCardBody = {
  title: ReactNode;
  meta?: ReactNode;
  status?: ReactNode;
  eyebrow?: ReactNode;
  detail?: ReactNode;
  sm: boolean;
};

/** The chip: title and meta on one line. */
function TaskCardLine({ title, meta, sm }: TaskCardBody) {
  return (
    <ItemContent className="flex-row items-baseline gap-2">
      <ItemTitle className={cn("text-foreground", sm ? "text-xs" : "text-meta")}>{title}</ItemTitle>
      {meta ? <span className="text-xs text-muted-foreground">{meta}</span> : null}
    </ItemContent>
  );
}

/** The card: eyebrow, title, status and meta, then the detail. */
function TaskCardStack({ title, meta, status, eyebrow, detail, sm }: TaskCardBody) {
  return (
    <ItemContent className="min-w-0 gap-1">
      {eyebrow ? (
        <span className="text-2xs font-semibold tracking-[0.06em] text-muted-foreground uppercase">
          {eyebrow}
        </span>
      ) : null}
      <ItemTitle className={cn("text-foreground", sm ? "text-xs" : "text-meta")}>{title}</ItemTitle>
      {status || meta ? (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {status}
          {meta ? <span>{meta}</span> : null}
        </span>
      ) : null}
      {detail ? <span className="line-clamp-2 text-xs text-muted-foreground">{detail}</span> : null}
    </ItemContent>
  );
}

const taskCardShape = (rich: boolean, sm: boolean) =>
  cn(
    "flex-nowrap self-start bg-card",
    rich ? "w-full max-w-[460px] items-start" : "w-fit",
    sm ? "gap-2 rounded-md px-2.5" : "gap-2.5 rounded-well px-3",
    rich ? (sm ? "py-2" : "py-2.5") : sm ? "py-1.5" : "py-2",
  );

/**
 * Link card to a task in its thread. `status` is its stage (a StatusLabel), rendered live from
 * the task; `eyebrow` names a card a person made ("New task · from Nadia"); `detail` is the
 * latest note, else what done looks like. With none of them, it's the compact chip.
 */
export function TaskCard({
  href,
  size = "md",
  ...body
}: Omit<TaskCardBody, "sm"> & { href: string; size?: ThreadSize }) {
  const sm = size === "sm";
  const rich = [body.status, body.eyebrow, body.detail].some((v) => v !== undefined);
  const Body = rich ? TaskCardStack : TaskCardLine;
  return (
    <Item
      data-slot="task-card"
      variant="outline"
      render={<Link href={href} />}
      className={taskCardShape(rich, sm)}
    >
      <ItemMedia className={cn(rich && "mt-0.5")}>
        <TasksIcon size={sm ? 13 : 14} className="text-foreground" />
      </ItemMedia>
      <Body {...body} sm={sm} />
    </Item>
  );
}

export type StepState = "done" | "running";

/** An agent's checklist of steps. */
export function StepList({ steps }: { steps: Array<{ label: string; state: StepState }> }) {
  return (
    <div data-slot="step-list" className="flex flex-col gap-1.5 rounded-md bg-muted px-3 py-2.5">
      {steps.map((step) => (
        <Marker key={step.label} className="min-h-4 gap-2 text-xs text-copy">
          <MarkerIcon>
            {step.state === "done" ? (
              <Check className="size-3.5 text-muted-foreground" />
            ) : (
              <DotMatrix className="size-3.5 text-moss" />
            )}
          </MarkerIcon>
          <MarkerContent
            className={cn(step.state === "running" && "shimmer motion-reduce:shimmer-none")}
          >
            {step.label}
          </MarkerContent>
        </Marker>
      ))}
    </div>
  );
}

/** "Checked 3 stores · 5 steps · 2 min" chip that opens to show the steps. */
export function StepsDisclosure({
  summary,
  children,
  defaultOpen = false,
}: {
  summary: ReactNode;
  children?: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="flex flex-col gap-2">
      <CollapsibleTrigger className="group/steps flex h-[26px] cursor-pointer items-center gap-2 self-start rounded-nav bg-muted px-2.5 text-xs text-copy hover:bg-foreground/[0.07] focus-visible:outline-2 focus-visible:outline-ring">
        <ChevronRight className="size-3 transition-transform group-data-[panel-open]/steps:rotate-90" />
        {summary}
      </CollapsibleTrigger>
      {children ? <CollapsibleContent>{children}</CollapsibleContent> : null}
    </Collapsible>
  );
}

export type LineItem = { label: ReactNode; detail?: ReactNode; value?: ReactNode };

/** Small priced list inside a message. */
export function LineItems({ items }: { items: LineItem[] }) {
  return (
    <div
      data-slot="line-items"
      className="flex flex-col rounded-tile bg-card shadow-ring [&>*+*]:border-t [&>*+*]:border-border"
    >
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-3 px-3.5 py-2.5">
          <span className="min-w-0 flex-1 text-nav font-medium text-foreground">{item.label}</span>
          <span className="w-[100px] shrink-0 text-meta text-muted-foreground">{item.detail}</span>
          <span className="w-12 shrink-0 text-right text-nav font-medium text-foreground tabular">
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// The dots light up from the top-left corner, one diagonal at a time.
const MATRIX = [0, 1, 2].flatMap((row) =>
  [0, 1, 2].map((col) => ({ x: 10 + col * 20, y: 10 + row * 20, delay: 60 + (row + col) * 45 })),
);

/** The working mark: a 3 × 3 grid of dots with a light sweeping across it. */
export function DotMatrix({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 60 60" aria-hidden className={cn("size-3.5 shrink-0", className)}>
      <g className="fill-current opacity-20">
        {MATRIX.map((dot) => (
          <circle key={`${dot.x}-${dot.y}`} cx={dot.x} cy={dot.y} r={6} />
        ))}
      </g>
      <g className="fill-current">
        {MATRIX.map((dot) => (
          <circle
            key={`${dot.x}-${dot.y}`}
            cx={dot.x}
            cy={dot.y}
            r={6}
            style={{ animationDelay: `${dot.delay}ms` }}
            className="origin-center animate-dot-sweep [transform-box:fill-box] motion-reduce:animate-none"
          />
        ))}
      </g>
    </svg>
  );
}

// Conductor's 18-dot cloud, lit in the order its light sweeps across.
const CLOUD = [
  [50, 10],
  [30, 30],
  [10, 50],
  [70, 10],
  [50, 30],
  [30, 50],
  [70, 30],
  [50, 50],
  [30, 70],
  [90, 30],
  [70, 50],
  [50, 70],
  [90, 50],
  [70, 70],
  [110, 50],
  [90, 70],
  [110, 70],
  [130, 70],
] as const;

/** The waking mark: a cloud of dots filling in, one dot at a time. */
export function DotCloud({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 140 80" aria-hidden className={cn("size-[18px] shrink-0", className)}>
      <g className="fill-current opacity-20">
        {CLOUD.map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r={7.2} />
        ))}
      </g>
      <g className="fill-current">
        {CLOUD.map(([x, y], i) => (
          <circle
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            r={7.2}
            style={{ animationDelay: `${i * 100}ms` }}
            className="origin-center animate-dot-fill [transform-box:fill-box] motion-reduce:animate-none"
          />
        ))}
      </g>
    </svg>
  );
}

/** "Working…" line for an agent mid-task: the moss dot matrix + shimmering label. */
export function WorkingIndicator({
  label = "Working",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <Marker
      role="status"
      data-slot="working-indicator"
      className={cn("w-auto gap-2 text-meta text-moss", className)}
    >
      <MarkerIcon className="flex w-[18px] items-center justify-center">
        <DotMatrix />
      </MarkerIcon>
      <MarkerContent className="shimmer motion-reduce:shimmer-none">{label}</MarkerContent>
    </Marker>
  );
}

/** "Waking your computer…" while a turn waits for a sleeping computer: the dot cloud fills in. */
export function WakingIndicator({
  label = "Waking your computer…",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <Marker
      role="status"
      data-slot="waking-indicator"
      className={cn("w-auto gap-2 text-meta text-moss", className)}
    >
      <MarkerIcon className="flex w-[18px] items-center justify-center">
        <DotCloud />
      </MarkerIcon>
      <MarkerContent className="shimmer motion-reduce:shimmer-none">{label}</MarkerContent>
    </Marker>
  );
}
